import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const LotusIcon = () => (
  <svg width="60" height="60" viewBox="0 0 100 100" fill="none">
    <ellipse cx="50" cy="75" rx="8" ry="20" fill="none" stroke="#C9A84C" strokeWidth="1.5" transform="rotate(-30 50 75)"/>
    <ellipse cx="50" cy="75" rx="8" ry="20" fill="none" stroke="#C9A84C" strokeWidth="1.5" transform="rotate(30 50 75)"/>
    <ellipse cx="50" cy="75" rx="8" ry="20" fill="none" stroke="#E8732A" strokeWidth="1.5"/>
    <ellipse cx="50" cy="75" rx="6" ry="18" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(-60 50 75)" opacity="0.6"/>
    <ellipse cx="50" cy="75" rx="6" ry="18" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(60 50 75)" opacity="0.6"/>
    <circle cx="50" cy="70" r="6" fill="rgba(232,115,42,0.2)" stroke="#E8732A" strokeWidth="1.5"/>
    <circle cx="50" cy="70" r="2" fill="#E8732A"/>
  </svg>
);

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="about" ref={ref} style={{
      padding: '120px 5%',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #111008 50%, #0D0D0D 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative side line */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: '20%',
        width: 3,
        height: '60%',
        background: 'linear-gradient(to bottom, transparent, #E8732A, transparent)',
        opacity: 0.4,
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Section header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 80 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to right, transparent, #C9A84C)' }} />
            <span style={{ fontFamily: 'Cinzel', fontSize: 11, letterSpacing: 4, color: '#C9A84C', textTransform: 'uppercase' }}>Our Story</span>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to left, transparent, #C9A84C)' }} />
          </div>
          <h2 style={{
            fontFamily: 'Cinzel',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: '#F5F0E8',
            letterSpacing: 2,
            marginBottom: 20,
          }}>
            About Vedic IT
          </h2>
          <p style={{ maxWidth: 600, margin: '0 auto', color: '#B0A898', lineHeight: 1.8, fontSize: 16 }}>
            Founded by experienced IT engineers who dared to question whether profit alone should define success in technology.
          </p>
        </motion.div>

        {/* Two column layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 60,
          alignItems: 'center',
          marginBottom: 80,
        }}>
          {/* Left: Story */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <LotusIcon />
            <h3 style={{ fontFamily: 'Cinzel', fontSize: 22, color: '#C9A84C', margin: '20px 0 16px', letterSpacing: 1 }}>
              Not Just Another IT Company
            </h3>
            <p style={{ color: '#B0A898', lineHeight: 1.9, marginBottom: 20, fontSize: 15 }}>
              We were engineers first — seasoned professionals who built systems, shipped products, and met deadlines. But somewhere along the way, we began asking deeper questions: <em style={{ color: '#F5F0E8' }}>What if technology could serve humanity's deepest needs, not just its surface desires?</em>
            </p>
            <p style={{ color: '#B0A898', lineHeight: 1.9, fontSize: 15 }}>
              Vedic IT was born at the intersection of ancient wisdom and modern innovation — where the timeless principles of dharma guide our code architecture, where mindfulness shapes our project planning, and where every build starts with clarity.
            </p>
          </motion.div>

          {/* Right: Mission card */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 12,
              padding: '40px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Corner accent */}
              <div style={{
                position: 'absolute', top: 0, right: 0,
                width: 80, height: 80,
                background: 'radial-gradient(circle at top right, rgba(232,115,42,0.12), transparent)',
              }} />
              <div style={{
                position: 'absolute', bottom: 0, left: 0,
                width: 60, height: 60,
                background: 'radial-gradient(circle at bottom left, rgba(201,168,76,0.08), transparent)',
              }} />

              <p style={{
                fontFamily: 'Playfair Display',
                fontSize: 20,
                color: '#F5F0E8',
                lineHeight: 1.7,
                fontStyle: 'italic',
                marginBottom: 24,
              }}>
                "We transform the IT industry through principles of ancient wisdom, where every digital solution carries the fingerprint of mindful, purposeful creation."
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 32, height: 1, background: '#E8732A' }} />
                <span style={{ fontFamily: 'Cinzel', fontSize: 12, letterSpacing: 2, color: '#E8732A' }}>CORE VISION</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ATMANIVAS callout */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          transition={{ duration: 0.7, delay: 0.6 }}
          style={{
            background: 'linear-gradient(135deg, rgba(232,115,42,0.06), rgba(201,168,76,0.06))',
            border: '1px solid rgba(232,115,42,0.2)',
            borderRadius: 12,
            padding: '40px 48px',
            display: 'flex',
            gap: 32,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '0 0 auto' }}>
            <div style={{ fontFamily: 'Cinzel', fontSize: 32, color: '#E8732A', opacity: 0.6 }}>🪔</div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h4 style={{ fontFamily: 'Cinzel', fontSize: 16, color: '#C9A84C', letterSpacing: 2, marginBottom: 10 }}>
              THE ATMANIVAS PROGRAM
            </h4>
            <p style={{ color: '#B0A898', lineHeight: 1.8, fontSize: 14 }}>
              Every engagement with Vedic IT supports our charitable mission — the ATMANIVAS Program, an end-of-life care initiative providing dignity, spiritual support, and community for elders on their final journey. Our commercial success directly funds this sacred work.
            </p>
          </div>
          <div style={{
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: '12px 24px',
            textAlign: 'center',
            flex: '0 0 auto',
          }}>
            <div style={{ fontFamily: 'Cinzel', fontSize: 20, color: '#C9A84C', marginBottom: 4 }}>80G</div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase' }}>Registered</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
